import dayjs from 'dayjs';
import { MouseEvent as ReactMouseEvent, RefObject, useCallback, useState } from 'react';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { CardResizeRange, ResizeEdge, TeachingBlockCardType } from '../types';

type UseTeachingBlockCardResizeProps = {
  card: TeachingBlockCardType;
  cardRef: RefObject<HTMLDivElement | null>;
  getPreviewGeometry: (range: CardResizeRange) => { left: string; width: string };
  range: CardResizeRange;
  isVirtual?: boolean;
  canResizeStart?: boolean;
  canResizeEnd?: boolean;
};

const getDateStringFromPoint = (clientX: number, clientY: number): string | undefined => {
  const elements = document.elementsFromPoint(clientX, clientY) as HTMLElement[];
  return elements.find((element) => element.dataset.dateString)?.dataset.dateString;
};

const clampDateStringToBounds = (
  targetDateString: string,
  bounds: { minDateString: string; maxDateString: string },
): string => {
  if (targetDateString < bounds.minDateString) return bounds.minDateString;
  if (targetDateString > bounds.maxDateString) return bounds.maxDateString;
  return targetDateString;
};

export const useTeachingBlockCardResize = ({
  card,
  cardRef,
  getPreviewGeometry,
  range,
  isVirtual = false,
  canResizeStart = true,
  canResizeEnd = true,
}: UseTeachingBlockCardResizeProps) => {
  const store = useTeachingBlockStore();
  const [activeEdge, setActiveEdge] = useState<ResizeEdge | null>(null);

  // Perf: Nur die GÜNSTIGE Resizebarkeits-Prüfung pro Render (O(1)). Die teuren
  // `getCardResizeBounds` (iterieren über alle Karten/Ferien) werden erst beim
  // tatsächlichen Resize-Start (mousedown) berechnet – nicht für jede Karte pro Render.
  const canAdjust = store.canAdjustCard(card);
  const resolvedCanResizeStart = canResizeStart && canAdjust;
  const resolvedCanResizeEnd = canResizeEnd && canAdjust;

  const createMouseDownHandler = useCallback(
    (edge: ResizeEdge) => (event: ReactMouseEvent<HTMLDivElement>) => {
      const allowed = edge === 'start' ? resolvedCanResizeStart : resolvedCanResizeEnd;
      if (event.button !== 0 || !allowed) return;

      // crossHolidays: über/in Ferien ziehen erlaubt – beim Loslassen entscheidet das
      // Time-Conflict-Modal (aufteilen/ignorieren).
      const bounds = store.getCardResizeBounds(card, edge, range, true);
      if (!bounds) return;

      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startY = event.clientY;
      let moved = false;
      let latestRange = range;
      let frameId: number | null = null;
      const previousUserSelect = document.body.style.userSelect;
      const previousCursor = document.body.style.cursor;

      setActiveEdge(edge);
      store.setResizingCard(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ew-resize';

      // Ziel-Datum der gezogenen Kante direkt am DOM zeigen (kein React-Rerender pro Frame),
      // damit man beim Verkürzen/Verlängern sieht, wo man ablegt.
      const showEdgeDate = (dateString: string) => {
        if (cardRef.current) cardRef.current.dataset.resizeDate = dayjs(dateString).format('DD.MM.YYYY');
      };
      showEdgeDate(edge === 'start' ? range.startDateString : range.endDateString);

      const cleanup = () => {
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
        document.body.style.userSelect = previousUserSelect;
        document.body.style.cursor = previousCursor;
        store.setResizingCard(false);
        setActiveEdge(null);
        cardRef.current?.style.removeProperty('--card-left-preview');
        cardRef.current?.style.removeProperty('--card-width-preview');
        cardRef.current?.removeAttribute('data-resize-date');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      const applyPreviewGeometry = (nextRange: CardResizeRange) => {
        const geometry = getPreviewGeometry(nextRange);
        if (!cardRef.current) return;

        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }

        const edgeDate = edge === 'start' ? nextRange.startDateString : nextRange.endDateString;
        frameId = requestAnimationFrame(() => {
          cardRef.current?.style.setProperty('--card-left-preview', geometry.left);
          cardRef.current?.style.setProperty('--card-width-preview', geometry.width);
          showEdgeDate(edgeDate);
          frameId = null;
        });
      };

      const updatePreview = (targetDateString: string) => {
        const clampedDateString = clampDateStringToBounds(targetDateString, bounds);
        latestRange =
          edge === 'start'
            ? { startDateString: clampedDateString, endDateString: range.endDateString }
            : { startDateString: range.startDateString, endDateString: clampedDateString };
        applyPreviewGeometry(latestRange);
      };

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        if (Math.abs(moveEvent.clientX - startX) > 2 || Math.abs(moveEvent.clientY - startY) > 2) {
          moved = true;
        }

        const targetDateString = getDateStringFromPoint(moveEvent.clientX, moveEvent.clientY);
        if (targetDateString) {
          updatePreview(targetDateString);
        }
      };

      const handleMouseUp = (upEvent: globalThis.MouseEvent) => {
        const targetDateString = getDateStringFromPoint(upEvent.clientX, upEvent.clientY);
        if (targetDateString) {
          updatePreview(targetDateString);
        }

        const finalTargetDateString = edge === 'start' ? latestRange.startDateString : latestRange.endDateString;
        if (!moved) {
          cleanup();
          return;
        }
        if (finalTargetDateString === (edge === 'start' ? range.startDateString : range.endDateString)) {
          cleanup();
          return;
        }

        const resizePromise = store.resizeCardEdge(card, edge, finalTargetDateString, range, isVirtual);
        cleanup();
        void resizePromise;
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [card, cardRef, getPreviewGeometry, isVirtual, range, resolvedCanResizeEnd, resolvedCanResizeStart, store],
  );

  const stopPropagation = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    activeEdge,
    canResizeStart: resolvedCanResizeStart,
    canResizeEnd: resolvedCanResizeEnd,
    startHandleProps: {
      onClick: stopPropagation,
      onMouseDown: createMouseDownHandler('start'),
    },
    endHandleProps: {
      onClick: stopPropagation,
      onMouseDown: createMouseDownHandler('end'),
    },
  };
};
